import type { HomePageType } from '@/domains/home/apis/home-api';
import { NAV_MENU_TYPE } from '@eureka/shared/nav-bar';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { E_ShareType } from '@eureka/services/src/types/share';
import { AuthService } from '@eureka/shared/share-tools/logic';
import { accountDeferred, addTrialDeferred, isUserLogin } from '@eureka/utils/account';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { createRouter, createWebHistory } from 'vue-router';
import { proxyRouterList } from './proxy-router';
import { deprecatedRouter } from './deprecated-router';
import { ipRouterList } from './ip-router';
import { HomeService } from '@/domains/home/services/home-service';
import { controlDeferred } from '@eureka/utils/control';
import { SiteRegion } from '@eureka/services/src/types/region';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            component: () => import('@/pages/Layout.vue'),
            redirect: '/home',
            children: [
                deprecatedRouter,
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/pages/ai/Ai.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    path: '/rd',
                    name: 'rd',
                    component: () => import('@/pages/home/RD.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                {
                    path: '/ls',
                    name: 'ls',
                    component: () => import('@/pages/home/LS.vue'),
                    beforeEnter: async(to, from, next) => {
                        const { isLsPlg } = await controlDeferred.promise;
                        if (isLsPlg) {
                            next('/ls/home')
                        } else {
                            next()
                        }
                    },
                },
                {
                    path: '/ls/home',
                    name: 'ls-home',
                    component: () => import('@/pages/home/LSHome.vue'),
                },
                {
                    path: '/mse',
                    name: 'mse',
                    component: () => import('@/pages/home/MSE.vue'),
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck();
                        next();
                    },
                },
                ...ipRouterList,
                {
                    path: '/explore',
                    name: 'explore',
                    component: () => import('@/pages/ai/Explore.vue'),
                    meta: {
                        navModuleType: NAV_MENU_TYPE.CUSTOM_AGENT,
                    },
                },
                {
                    path: '/user-profile',
                    name: 'user-profile',
                    component: () => import('@/pages/UserProfile.vue'),
                },
                {
                    name: 'seek',
                    path: '/page/:seek_id',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.seek_id as string,
                            data_type: E_ShareType.SEEK_SEARCH,
                        });
                        next();
                    },
                    component: () => import('@/pages/seek/Page.vue'),
                },
                {
                    name: 'generalSearch',
                    path: '/general-search',
                    component: () => import('@/pages/general-search/index.vue'),
                },
                {
                    name: 'materialsGeneralSearch',
                    path: '/materials-general-search',
                    component: () => import('@/pages/materials-general-search/index.vue'),
                },
                {
                    name: 'ai-ip',
                    path: '/ai-ip',
                    children: [
                        {
                            path: 'image-fto-search/:agent_job_id?',
                            name: 'ai-ip-image-fto-search',
                            component: () => import('@/pages/ai-ip/ImageFTOSearch.vue'),
                        }
                    ],
                },
                {
                    name: 'ai-ls',
                    path: '/ai-ls',
                    children: [
                        {
                            path: ':lsAgentName',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-entry',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lsAgentName/:lsAgentId',
                            meta: {
                                navModuleType: NAV_MENU_TYPE.LS_CHAT,
                            },
                            name: 'ai-ls-agent-detail',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        },
                        {
                            path: ':lifesciences*',
                            name: 'ai-ls-page',
                            component: () => import('@/pages/ai-ls/AiLs.vue'),
                        }
                    ],
                },
                {
                    name: 'workflow',
                    path: '/material/workflow',
                    component: () => import('@/pages/material/workflow/index.vue'),
                },
                {
                    name: 'substance',
                    path: '/material/substance',
                    component: () => import('@/pages/material/substance/index.vue'),
                },
                {
                    name: 'property',
                    path: '/material/property',
                    component: () => import('@/pages/material/property/index.vue'),
                },
                {
                    name: 'structure',
                    path: '/material/structure',
                    component: () => import('@/pages/material/structure/index.vue'),
                },
                {
                    name: 'metal',
                    path: '/material/metal',
                    component: () => import('@/pages/material/metal/index.vue'),
                },
                {
                    name: 'grade',
                    path: '/material/grade/:grade_id',
                    component: () => import('@/pages/material/grade/index.vue'),
                },
                {
                    name: 'mse-doc-analyzer',
                    path: '/material/analyzer',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'mse-doc-analyzer-result',
                    path: '/material/analyzer/:lsAgentId',
                    component: () => import('@/pages/material/doc-analyzer/MSEDocAnalyzer.vue'),
                },
                {
                    name: 'userSetting',
                    path: '/user-setting',
                    component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Layout.vue'),
                    beforeEnter: async(to, from, next) => {
                        if (to.name === 'userSettingPackage') {
                            try {
                                const homeService = injectService<HomeService>(HomeService);
                                const data = await homeService.getHomePageType();
                                return next({
                                    name: `userSettingPackage${data.homepage_type}`,
                                    query: to.query,
                                })
                            } catch (error) {
                                return next({
                                    name: 'userSettingPackageRD',
                                    query: to.query,
                                })
                            }
                        }
                        next();
                    },
                    children: [
                        {
                            path: 'amount',
                            name: 'userSettingAmount',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Amount.vue'),
                        },
                        {
                            path: 'package',
                            name: 'userSettingPackage',
                            component: () => import(/* webpackChunkName: "air-usersetting" */'@/pages/user-setting/Package.vue'),
                        },
                        {
                            path: 'package/rd',
                            name: 'userSettingPackageRD',
                            component: () => import(/* webpackChunkName: "air-usersetting-RD" */'@/pages/user-setting/new-version/product-lines/RD.vue'),
                        }, {
                            path: 'package/ip',
                            name: 'userSettingPackageIP',
                            component: async() => {
                                const data = await accountDeferred.promise;
                                if (data.siteRegion === SiteRegion.CN) {
                                    return import(/* webpackChunkName: "air-usersetting-IP" */'@/pages/user-setting/new-version/product-lines/IP.vue')
                                }
                                return import(/* webpackChunkName: "air-usersetting-IpPlg" */'@/pages/user-setting/new-version/product-lines/IpPlg.vue')
                            },
                        }, {
                            path: 'package/mse',
                            name: 'userSettingPackageMSE',
                            component: () => import(/* webpackChunkName: "air-usersetting-MSE" */'@/pages/user-setting/new-version/product-lines/MSE.vue'),
                        }, {
                            path: 'package/ls',
                            name: 'userSettingPackageLS',
                            component: () => import(/* webpackChunkName: "air-usersetting-LS" */'@/pages/user-setting/new-version/product-lines/LS.vue'),
                        }, {
                            path: 'package/ls/plg',
                            name: 'userSettingPackageLS_PLG',
                            component: () => import(/* webpackChunkName: "air-usersetting-LSPlg" */'@/pages/user-setting/new-version/product-lines/LsPlg.vue'),
                        }
                    ],
                },
                {
                    name: 'roadmap',
                    path: 'roadmap',
                    children: [
                        {
                            path: '',
                            name: 'roadmapHome',
                            component: () => import('@/pages/roadmap/index.vue'),
                        },
                        {
                            path: 'create',
                            name: 'roadmapCreate',
                            component: () => import('@/pages/roadmap/create.vue'),
                        }
                    ],
                },
                {
                    name: 'CompetitiveLandscape',
                    path: '/competitive-landscape',
                    component: () => import('@/pages/competitive-landscape/index.vue'),
                },
                {
                    name: 'CompanySearch',
                    path: '/company-search',
                    component: () => import('@/pages/company-search/index.vue'),
                },
                {
                    name: 'graph',
                    path: '/graph',
                    meta: {
                        navModuleType: NAV_MENU_TYPE.WORK_PROJECT,
                    },
                    children: [
                        {
                            path: '',
                            name: 'GraphHome',
                            component: () => import('@/pages/graph/index.vue'),
                        },
                        {
                            path: ':graph_id',
                            name: 'GraphDetail',
                            component: () => import('@/pages/graph/detail.vue'),
                            beforeEnter: async(to, from, next) => {
                                const authService = injectService<AuthService>(AuthService);
                                await authService.shareAccessCheck({
                                    data_id: to.params.graph_id as string,
                                    data_type: E_ShareType.PROJECT,
                                });
                                next();
                            },
                        },
                        {
                            path: ':graph_id/report',
                            name: 'GraphReport',
                            component: () => import('@/pages/graph/report.vue'),
                        },
                        {
                            path: ':graph_id/pdf/:item_id',
                            name: 'GraphPdf',
                            component: () => import('@/pages/graph/pdf.vue'),
                        }
                    ],
                },
                {
                    path: '/403',
                    component: () => import('@/pages/403/Unauthorized.vue'),
                },
                {
                    path: '/404',
                    component: () => import('@/pages/404/NotFound.vue'),
                }
            ],
        },
        {
            name: 'onboarding',
            path: '/onboarding',
            component: () => import('@/pages/Onboarding.vue'),
        },
        {
            path: '/ai',
            name: 'ai',
            redirect: '/home',
            component: () => import('@/pages/Layout.vue'),
            meta: {
                navModuleType: NAV_MENU_TYPE.AI_GENERATE,
            },
            children: [
                {
                    path: '/ai/agent',
                    name: 'ai-agent',
                    redirect: '/home',
                },
                {
                    path: '/ai/agent-flow/:agent_job_id',
                    name: 'ai-agent-flow',
                    beforeEnter: async(to, from, next) => {
                        const authService = injectService<AuthService>(AuthService);
                        await authService.shareAccessCheck({
                            data_id: to.params.agent_job_id as string,
                            data_type: E_ShareType.AI_AGENT,
                        });
                        next();
                    },
                    component: () => import('@/pages/ai/AgentFlow.vue'),
                }
            ],
        },
        ...proxyRouterList,
        // 通配符路由 - 捕获所有未匹配的路径并重定向到404页面
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            redirect: '/404',
        }
    ],
});

// 各产品首页路由配置。 如要修改，请同步修改/packages/air/src/main.ts的detailPageRegExps数组 ！！！
const router_map: Record<HomePageType, string> = {
    RD: 'rd',
    LS: 'ls',
    MSE: 'mse',
    IP: 'ip',
    LS_PLG: 'ls/home',
}

router.beforeEach(async(to, from, next) => {
    // 游客版跳转旧首页(/home);
    if (Object.values(router_map).map(ele => `/${ele}`).includes(to.path)) {
        const isGuest = !await isUserLogin();
        const hostname = window.location.hostname;
        if (isGuest && (hostname.startsWith('eureka.zhihuiya.com') || !to.path.startsWith(`/${router_map.LS_PLG}`))) {
            next('/home');
            return;
        }
    }
    if (to.path === '/home' || to.path === '/home/') {
        try {
            const isGuest = !await isUserLogin();
            if (isGuest) {
                next()
                return;
            }
            await addTrialDeferred.promise;
            const onboardingData = await CommonApis.getUserIsNeedOnBoarding();

            if (onboardingData.required) {
                next()
                return;
            }

            const homeService = injectService<HomeService>(HomeService);
            const data = await homeService.getHomePageType();
            if (data.homepage_type) {
                next(`/${router_map[data.homepage_type]}`);
            } else {
                next()
            }
        } catch (error) {
            next()
        }
    } else {
        next()
    }
});

export default router;
